import { resolveProgramCode, resolveProgramName } from '../../../utils/dataNormalizer';

// Shared report-view helpers live here so multiple runtime components can agree
// on dataset lookup, nested-field access, sort defaults, and detail-drawer row shaping.
export const buildDatasetMap = (datasets = []) => {
  const map = new Map();
  datasets.forEach((dataset) => {
    if (dataset?.id) {
      map.set(dataset.id, dataset);
    }
  });
  return map;
};

export const resolveInitialSortModel = (definition = {}) => {
  const defaultSort = definition?.defaultSort;
  if (!defaultSort?.field) return [];
  const direction = String(defaultSort.direction || defaultSort.sort || 'desc').toLowerCase();
  return [{
    field: defaultSort.field,
    sort: direction === 'asc' ? 'asc' : 'desc',
  }];
};

export const scopeColor = (scope) => {
  if (!scope) return 'default';
  const normalized = scope.toLowerCase();
  if (normalized === 'dpe') return 'primary';
  if (normalized === 'appsheet') return 'secondary';
  if (normalized === 'combined') return 'info';
  return 'default';
};

export const getFieldValue = (row, field) => {
  if (!field) return undefined;
  const key = String(field);
  if (row && Object.prototype.hasOwnProperty.call(row, key)) return row[key];
  if (!key.includes('.')) return row?.[key];
  const segments = key
    .split('.')
    .map((segment) => segment.trim())
    .filter(Boolean);
  return segments.reduce((acc, segment) => (acc == null ? acc : acc[segment]), row);
};

export const formatRowsForOutput = (rows, fields) =>
  rows.map((row) =>
    fields.reduce((acc, field) => {
      const value = getFieldValue(row, field);
      if (value !== undefined) acc[field] = value;
      return acc;
    }, {})
  );

const getPreferredValue = (values) => {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    return value;
  }
  return null;
};

const toNonEmptyString = (value) => {
  if (value === undefined || value === null) return null;
  if (typeof value === 'object') return null;
  const normalized = String(value).trim();
  return normalized.length ? normalized : null;
};

export const buildDetailRowCandidate = (row, params = {}) => {
  if (!row || typeof row !== 'object') return null;

  const bacField = params?.bacField || 'bac';
  const programField = params?.programField || 'programName';
  const programCodeField = params?.programCodeField || 'programCode';
  const programLabelField = params?.programLabelField || 'programLabel';
  const selectionKeyField = params?.selectionKeyField || 'selectionKey';

  const bacRaw = getPreferredValue([
    getFieldValue(row, bacField),
    row.bac,
    row.dealerCd,
  ]);
  const bac = bacRaw == null ? null : String(bacRaw).trim();

  const rawProgramName = toNonEmptyString(getPreferredValue([
    getFieldValue(row, programField),
    row.programName,
    row.programLabel,
  ]));
  const rawProgramCode = toNonEmptyString(getPreferredValue([
    getFieldValue(row, programCodeField),
    row.programCode,
    row.program,
  ]));
  const rawProgramLabel = toNonEmptyString(getPreferredValue([
    getFieldValue(row, programLabelField),
    row.programLabel,
  ]));

  const resolvedProgramName =
    resolveProgramName(rawProgramName) ||
    resolveProgramName(rawProgramCode) ||
    (rawProgramName == null ? null : String(rawProgramName).trim());

  const resolvedProgramCode = rawProgramCode || resolveProgramCode(resolvedProgramName);

  const selectionKey = getPreferredValue([
    getFieldValue(row, selectionKeyField),
    row.selectionKey,
  ]);

  // Many report rows are not native enrollment rows, so this helper opportunistically
  // reconstructs enough dealer/program context to open the standard detail drawer.
  const dpeEnrollment = row?.dpe?.enrollment && typeof row.dpe.enrollment === 'object'
    ? row.dpe.enrollment
    : null;
  const dpeSelection = row?.dpe?.selection && typeof row.dpe.selection === 'object'
    ? row.dpe.selection
    : null;
  const productSelections = Array.isArray(dpeEnrollment?.productSelections)
    ? dpeEnrollment.productSelections
    : (dpeSelection ? [dpeSelection] : undefined);
  const vendors = Array.isArray(dpeEnrollment?.vendors) ? dpeEnrollment.vendors : undefined;
  const enrollmentStatusCd = toNonEmptyString(
    getPreferredValue([row?.enrollmentStatus, dpeEnrollment?.enrollmentStatusCd, row?.enrollmentStatusCd])
  );
  const dealerName = toNonEmptyString(
    getPreferredValue([row?.dealerName, dpeEnrollment?.dealerName])
  );

  return {
    ...row,
    ...(dealerName ? { dealerName } : {}),
    ...(enrollmentStatusCd ? { enrollmentStatusCd } : {}),
    ...(productSelections ? { productSelections } : {}),
    ...(vendors ? { vendors } : {}),
    bac,
    programName: resolvedProgramName,
    programCode: resolvedProgramCode,
    program: toNonEmptyString(row.program) || resolvedProgramCode,
    programLabel: rawProgramLabel || resolvedProgramName,
    selectionKey: selectionKey || row.selectionKey || undefined,
  };
};
